import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useQueueStore } from "@/lib/store";
import { MasterPatient, Consultation } from "@/types";
import { 
  Search, 
  User, 
  Phone, 
  Calendar, 
  MapPin, 
  Clock,
  CheckCircle,
  AlertCircle,
  Eye,
  History
} from "lucide-react";
import { formatPatientId } from '@/lib/utils/patientId';
import { safeFormatDateOnly } from '@/lib/utils/dateUtils';
import { PatientHistoryDialog } from './PatientHistoryDialog';

interface PatientSearchDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectPatient?: (masterPatient: MasterPatient) => void;
  showHistoryButton?: boolean;
}

export function PatientSearchDialog({ 
  isOpen, 
  onClose, 
  onSelectPatient,
  showHistoryButton = true 
}: PatientSearchDialogProps) {
  const { 
    masterPatients, 
    fetchMasterPatients,
    consultations,
    fetchConsultations
  } = useQueueStore();

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<MasterPatient[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<MasterPatient | null>(null);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchMasterPatients();
      fetchConsultations();
    }
  }, [isOpen, fetchMasterPatients, fetchConsultations]);

  const handleSearch = () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    const query = searchQuery.toLowerCase().trim();
    
    // Search by name or phone number
    const results = masterPatients.filter(patient => 
      patient.name.toLowerCase().includes(query) ||
      patient.contactNumber.includes(query)
    );

    // Sort by most recent activity first
    const resultsWithActivity = results.map(patient => {
      const patientConsultations = consultations.filter(c => c.masterPatientId === patient.id);
      const lastActivity = patientConsultations.length > 0 ? 
        new Date(Math.max(...patientConsultations.map(c => new Date(c.registeredAt).getTime()))) : 
        new Date(patient.createdAt);
      
      return { ...patient, lastActivity };
    });

    const sortedResults = resultsWithActivity.sort((a, b) => 
      b.lastActivity.getTime() - a.lastActivity.getTime()
    );

    setSearchResults(sortedResults.slice(0, 10)); // Limit to 10 results
    setIsSearching(false);
  };

  const handleSelectPatient = (patient: MasterPatient) => {
    if (onSelectPatient) {
      onSelectPatient(patient);
      onClose();
    } else {
      setSelectedPatient(patient);
    }
  };

  const handleViewHistory = (patient: MasterPatient) => {
    setSelectedPatient(patient);
    setShowHistoryDialog(true);
  };

  const getPatientStats = (patientId: string) => {
    const patientConsultations = consultations.filter(c => c.masterPatientId === patientId);
    const totalVisits = patientConsultations.length;
    const completedVisits = patientConsultations.filter(c => c.status === 'completed').length;
    const lastVisit = patientConsultations.length > 0 ? 
      new Date(Math.max(...patientConsultations.map(c => new Date(c.registeredAt).getTime()))) : null;
    
    return { totalVisits, completedVisits, lastVisit };
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in-consultation': return 'bg-blue-100 text-blue-800';
      case 'waiting': return 'bg-yellow-100 text-yellow-800';
      case 'no-show': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search className="h-5 w-5" />
              Search Patients
            </DialogTitle>
            <DialogDescription>
              Search for patients by name, phone number, or patient ID.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Search Input */}
            <div className="flex gap-2">
              <Input
                placeholder="Search by patient name or phone number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="flex-1"
              />
              <Button 
                onClick={handleSearch} 
                disabled={isSearching}
                variant="outline"
              >
                {isSearching ? "Searching..." : "Search"}
              </Button>
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="space-y-3">
                <h3 className="font-medium text-gray-700">
                  Found {searchResults.length} patient(s)
                </h3>
                
                {searchResults.map((patient) => {
                  const stats = getPatientStats(patient.id);
                  const latestConsultation = consultations
                    .filter(c => c.masterPatientId === patient.id)
                    .sort((a, b) => new Date(b.registeredAt).getTime() - new Date(a.registeredAt).getTime())[0];
                  
                  return (
                    <Card key={patient.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 flex-1">
                            <User className="h-8 w-8 text-gray-500" />
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-medium">{patient.name}</h4>
                                <Badge variant="outline" className="text-xs">
                                  {formatPatientId(patient.id)}
                                </Badge>
                              </div>
                              <div className="text-sm text-gray-600 space-y-1">
                                <div className="flex items-center gap-4">
                                  <span className="flex items-center gap-1">
                                    <Phone className="h-3 w-3" />
                                    {patient.contactNumber}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Calendar className="h-3 w-3" />
                                    {patient.age}, {patient.gender}
                                  </span>
                                  {patient.location && (
                                    <span className="flex items-center gap-1">
                                      <MapPin className="h-3 w-3" />
                                      {patient.location}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            {/* Patient Stats */}
                            <div className="text-right text-sm">
                              <div className="flex items-center gap-2">
                                <CheckCircle className="h-3 w-3 text-green-600" />
                                <span>{stats.completedVisits}/{stats.totalVisits} visits</span>
                              </div>
                                                             {stats.lastVisit && (
                                 <div className="text-gray-500">
                                   Last: {safeFormatDateOnly(stats.lastVisit.toISOString())}
                                 </div>
                               )}
                            </div>
                            
                            {/* Action Buttons */}
                            <div className="flex gap-1">
                              {showHistoryButton && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleViewHistory(patient)}
                                  className="flex items-center gap-1"
                                >
                                  <History className="h-3 w-3" />
                                  History
                                </Button>
                              )}
                              
                              {onSelectPatient && (
                                <Button
                                  size="sm"
                                  onClick={() => handleSelectPatient(patient)}
                                  className="flex items-center gap-1"
                                >
                                  <Eye className="h-3 w-3" />
                                  Select
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Latest Visit Info */}
                        {latestConsultation && (
                          <div className="mt-3 pt-3 border-t">
                            <div className="flex items-center justify-between text-sm">
                              <div className="flex items-center gap-2">
                                <Clock className="h-3 w-3 text-gray-500" />
                                <span className="text-gray-600">Latest visit:</span>
                                <span>{latestConsultation.reason}</span>
                              </div>
                              <Badge className={getStatusColor(latestConsultation.status)}>
                                {latestConsultation.status}
                              </Badge>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* No Results */}
            {searchQuery && searchResults.length === 0 && !isSearching && (
              <div className="text-center py-8 text-gray-500">
                <Search className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No patients found matching "{searchQuery}"</p>
                <p className="text-sm mt-2">Try searching by name or phone number</p>
              </div>
            )}

            {/* Initial State */}
            {!searchQuery && (
              <div className="text-center py-8 text-gray-500">
                <Search className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>Search for patients by name or phone number</p>
                <p className="text-sm mt-2">Results will show patient history and recent activity</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Patient History Dialog */}
      {selectedPatient && (
        <PatientHistoryDialog
          masterPatient={selectedPatient}
          isOpen={showHistoryDialog}
          onClose={() => {
            setShowHistoryDialog(false);
            setSelectedPatient(null);
          }}
        />
      )}
    </>
  );
} 